/**
 * Panel — consistent chrome around any markets dashboard widget.
 *
 * Wraps content in the dark/cyan/mono aesthetic used by the landing page
 * so all widgets feel like part of the same dashboard regardless of how
 * the underlying widget styles its content.
 */
/* eslint-disable eqeqeq */
import React from 'react';
import { Box, Typography } from '@mui/material';

const Panel = ({ eyebrow, title, count, action, children, padded = true }) => (
  <Box
    sx={{
      backgroundColor: 'var(--dwlf-surface)',
      border: '1px solid var(--dwlf-ink-08)',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}
  >
    {(eyebrow || title) && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 3, md: 4 },
          py: 2.5,
          borderBottom: '1px solid var(--dwlf-ink-06)',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.6rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'var(--dwlf-accent-60)',
                mb: 0.5,
              }}
            >
              {eyebrow}
            </Typography>
          )}
          {title && (
            <Typography
              sx={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: '1.1rem',
                fontWeight: 500,
                color: 'var(--dwlf-text)',
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
          )}
        </Box>
        {(count != null || action) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {count != null && (
              <Typography
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.7rem',
                  color: 'var(--dwlf-ink-40)',
                  letterSpacing: '0.1em',
                }}
              >
                {count}
              </Typography>
            )}
            {action}
          </Box>
        )}
      </Box>
    )}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        p: padded ? { xs: 3, md: 4 } : 0,
        overflow: 'auto',
        // Tame any nested MUI Card chrome that the legacy widgets may render
        '& > .MuiCard-root, & .MuiCard-root': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          border: 'none',
        },
        '& .MuiCardContent-root': {
          padding: '0 !important',
        },
        '& .MuiCardHeader-root': {
          padding: 0,
          marginBottom: 2,
        },
      }}
    >
      {children}
    </Box>
  </Box>
);

export default Panel;
